/**
 * This hook is used to fetch equipments from the server based on the search string.
 */

import React, { useEffect, useState, useRef } from 'react';
import debounce from 'lodash.debounce';

import { TwAutocompleteOption } from '../components/TwAutocomplete/TwAutocomplete';
import { useLazySearchEquipmentsQuery } from '../redux/api/equipment/equipmentApi';
import useNotifications from '../providers/Notifications/Notifications.provider';
import { Equipment, EquipmentType } from '../redux/api/equipment/equipment.model';
import StringUtils from '../utils/stringUtils';

const useEquipmentSearch = (
  equipmentType?: EquipmentType,
  initialFetchLimit?: number
): [
  TwAutocompleteOption[],
  React.Dispatch<React.SetStateAction<TwAutocompleteOption[]>>,
  (search: string | undefined) => void
] => {
  const [equipmentLabels, setEquipmentLabels] = useState<TwAutocompleteOption[]>([]);
  const [searchEquipment] = useLazySearchEquipmentsQuery();
  const { addNotification } = useNotifications();

  const debouncedSearch = useRef(
    // wait for 300 millisecond after last keystroke before api call
    debounce((search: string) => {
      searchEquipment({
        params: {
          search
        },
        filters: equipmentType ? { equipmentType: [equipmentType] } : {}
      })
        .then((response) => {
          if (!response) return;
          const labels: TwAutocompleteOption[] = [];
          response?.data?.equipments?.map((equipment: Equipment) => {
            labels.push({
              id: equipment.id,
              label: equipment.name,
              value: equipment.name,
              subLabel: StringUtils.capitalizeFirstLetter(equipment.type.toLowerCase())
            });
          });
          setEquipmentLabels(labels);
        })
        .catch((error) => {
          console.error('Error while fetching equipments', error);
          addNotification({
            message: 'Error while fetching equipments',
            variant: 'error'
          });
        });
    }, 300)
  ).current;

  const fetchInitialEquipmentData = async (): Promise<void> => {
    try {
      const response = await searchEquipment({
        params: {
          limit: initialFetchLimit || 100
        },
        filters: equipmentType ? { equipmentType: [equipmentType] } : {}
      });
      if (!response) return;
      const labels: TwAutocompleteOption[] =
        response?.data?.equipments?.map((equipment: Equipment) => ({
          id: equipment.id,
          label: equipment.name,
          value: equipment.name,
          subLabel: StringUtils.capitalizeFirstLetter(equipment.type.toLowerCase())
        })) || [];
      setEquipmentLabels(labels);
    } catch (error) {
      console.error('Error while fetching equipments', error);
      addNotification({
        message: 'Error while fetching equipments',
        variant: 'error'
      });
    }
  };

  useEffect(() => {
    fetchInitialEquipmentData();

    return (): void => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch, equipmentType, initialFetchLimit]);

  const fetchEquipments = (search?: string): void => {
    if (!search) {
      debouncedSearch.cancel();
      setEquipmentLabels([]);
      return;
    }
    debouncedSearch(search);
  };

  return [equipmentLabels, setEquipmentLabels, fetchEquipments];
};

export default useEquipmentSearch;
